import React from 'react';
import GoogleMapReact from 'google-map-react';

import { Marker } from './Marker';

import styles from './Map.module.css';
import { ShadowWrapper } from '../ShadowWrapper/ShadowWrapper';

type Place = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  avatarUrl: string | undefined;
};

type MapProps = {
  places: Array<Place>;
  center: Place;
  className?: string;
};

export const Map: React.FC<MapProps> = ({ places, center, className }) => {
  // Return map bounds based on list of places
  const getMapBounds = (map: any, maps: any, places: Array<Place>) => {
    const bounds = new maps.LatLngBounds();
    bounds.extend(new maps.LatLng(center.lat, center.lng));

    places.forEach((place: Place) => {
      bounds.extend(new maps.LatLng(place.lat, place.lng));
    });
    return bounds;
  };

  // Re-center map when resizing the window
  const bindResizeListener = (map: any, maps: any, bounds: any) => {
    maps.event.addDomListenerOnce(map, 'idle', () => {
      maps.event.addDomListener(window, 'resize', () => {
        map.fitBounds(bounds);
      });
    });
  };

  // Fit map to its bounds after the api is loaded
  const apiIsLoaded = (map: any, maps: any, places: Array<Place>) => {
    // Get bounds by our places
    const bounds = getMapBounds(map, maps, places);
    // Fit map to bounds
    map.fitBounds(bounds);
    // Bind the resize listener
    bindResizeListener(map, maps, bounds);
  };

  return (
    <ShadowWrapper className={className ? styles[className] : styles.map}>
      <GoogleMapReact
        bootstrapURLKeys={
          process.env.REACT_APP_MAP_API_KEY
            ? { key: process.env.REACT_APP_MAP_API_KEY }
            : undefined
        }
        zoom={10}
        center={{ lat: center.lat, lng: center.lng }}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) => apiIsLoaded(map, maps, places)}
      >
        <Marker
          key={center.id}
          text={center.name}
          meta={center.avatarUrl}
          lat={center.lat}
          lng={center.lng}
          isCenter
        />
        {places.map((place: Place) => (
          <Marker
            key={place.id}
            text={place.name}
            meta={place.avatarUrl}
            lat={place.lat}
            lng={place.lng}
          />
        ))}
      </GoogleMapReact>
    </ShadowWrapper>
  );
};
